
import React from 'react';
import { ExternalLink, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { useIsMobile } from '@/hooks/use-mobile';

interface ConnectAccountCardProps {
  name: string;
  logo: string;
  description: string;
  connected?: boolean;
  className?: string;
  isSupported?: boolean;
}

export const ConnectAccountCard = ({
  name,
  logo,
  description,
  connected = false,
  className,
  isSupported = false
}: ConnectAccountCardProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const logoSize = isMobile ? 'h-6 w-6' : 'h-8 w-8';

  return (
    <div className={cn(
      "bg-card rounded-lg p-4 md:p-5 border border-border transition-all hover:border-primary/30",
      className
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 md:space-x-3 max-w-[75%]">
          <div className={`${logoSize} rounded-md overflow-hidden`}>
            <ImageWithFallback 
              src={logo} 
              alt={`${name} logo`} 
              className="h-full w-full object-contain"
              showSkeleton={true}
              skeletonClassName="bg-muted/50"
              priority={name === 'Binance' ? 'high' : 'low'}
            />
          </div>
          <h3 className="font-medium truncate">{name}</h3>
        </div>
        {connected ? (
          <Badge variant="default" className="bg-primary/20 text-primary hover:bg-primary/30 ml-2 whitespace-nowrap">
            {t('status_active')}
          </Badge>
        ) : !isSupported && (
          <Badge variant="outline" className="bg-muted text-muted-foreground border-muted-foreground/20 ml-2 whitespace-nowrap">
            {isMobile ? t('soon') : t('coming_soon')}
          </Badge>
        )}
      </div>
      
      <p className="text-xs md:text-sm text-muted-foreground mb-4 line-clamp-3 md:line-clamp-none">{description}</p>
      
      <div className="flex justify-end">
        {!isSupported ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size={isMobile ? "sm" : "sm"} 
                  disabled 
                  className="text-muted-foreground"
                >
                  <Plus size={16} className="mr-1" />
                  <span>{t('connect')}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('coming_soon_tooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button variant={connected ? "outline" : "default"} size={isMobile ? "sm" : "sm"}>
            {connected ? (
              <>
                <span>{t('manage')}</span>
                <ExternalLink size={14} className="ml-1" />
              </>
            ) : (
              <>
                <Plus size={16} className="mr-1" />
                <span>{t('connect')}</span>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
